/**
 * The frame box — the one place the app says how wide and how tall a screen is.
 *
 * ── THE DEFECT THIS FILE ENDS ───────────────────────────────────────────────────
 * Every screen was drawn in `w-[360px] h-[680px]`. That is not a layout; it is the
 * ARTBOARD the design document draws a phone on, transcribed literally into the running
 * app. Six frame components each declared their own copy of it.
 *
 * Both symptoms the founder reported are that one box:
 *
 *   "ল্যাপটপ দিয়ে ঢুকলেও মোবাইলের মত দেখায়"  — because it is literally a 360px column,
 *                                              at every window size, forever.
 *   "মোবাইল দিয়ে ঢুকলে ফুল ভিউ আসে না কেটে থাকে" — because 680px is not the height of any
 *                                              real phone window. A browser's visible
 *                                              area is shorter than its screen by the
 *                                              address bar, so the bottom of the box is
 *                                              cut, and on a 412px-wide phone the app
 *                                              sits in a 360px column with dead bands
 *                                              down both sides.
 *
 * ── WHY NO GATE CAUGHT IT, WHICH IS THE PART WORTH REMEMBERING ──────────────────
 * The visual gate scores 72 screens at 2% and they all pass. It captures each screen at
 * 360×680 — the size the frame declares — so it was measuring the artboard against the
 * artboard. A box that is always 360×680 is always 360×680. The gate was not weak; it
 * was answering a different question, and nothing was asking this one until
 * `tools/responsive/measure.mjs` did.
 *
 * ── WHAT THIS CHANGES, AND WHAT IT DELIBERATELY DOES NOT ────────────────────────
 * Width becomes fluid between the two widths the design actually draws. Below 360 the
 * frame shrinks instead of overflowing — at 320 every screen used to push 40px off the
 * right edge, which on a phone is content that simply does not exist, because nobody
 * finds a horizontal scrollbar. Between 360 and 430 it fills the phone, which is what
 * "full view" means and what the scaling table was built for.
 *
 * Above 430 it stops. That is not laziness and not an oversight:
 * `CONSUMER_HAS_DESKTOP_LAYOUT === false` in `breakpoints.ts`, from the design's own
 * words — "ডেস্কটপ দরকার অপারেশন টিমের জন্য, আর সেখানে মোবাইল লেআউট টেনে বড় করা হয় না;
 * আলাদা কাঠামো." A consumer screen stretched to 1440 is a redesign nobody drew, and
 * docs/01 makes the design file normative. So on a desktop the phone column is CENTRED
 * ON THE CANVAS and presented deliberately. What a consumer screen should look like on
 * a laptop is a design question, and it is flagged as one rather than invented here.
 *
 * Height stops being a number and becomes the window. `h-[100svh]` replaces `h-[680px]`,
 * so a screen is exactly as tall as the device it is on rather than as tall as a picture
 * of a device.
 *
 * ── WHY THE FRAME FILLS THE WINDOW RATHER THAN GROWING WITH ITS CONTENT ─────────
 * `min-h` was tried first and it is wrong, which the visual gate said immediately: nine
 * screens moved by 3–9%. A frame that grows makes the PAGE scroll, and the tab bar and
 * the teal header band are drawn pinned to the frame — so they scrolled away, which is
 * not an app.
 *
 * The content region scrolls instead (`.oprappo-scroll` in `styles.css`, which explains
 * why the design's own `overflow:hidden` means the opposite thing in a running app).
 * Header and tab bar stay pinned, the middle moves, and the drawn composition holds at
 * every window height instead of only at 680.
 *
 * ── HOW THE 2% GATE STAYS MEANINGFUL ────────────────────────────────────────────
 * At a 360×680 viewport every value below computes to exactly what it replaced: `w-full`
 * with a 430 cap is 360, and `min-h-[100svh]` is 680. So the visual gate does not go
 * green by being relaxed — it stays green because at the width the design was drawn at,
 * nothing moved. That is the gate proving the change rather than tolerating it.
 */

/**
 * The widest phone frame the design draws. Not a guess: `PHONE_WIDTHS` is [360, 390, 430]
 * and 430 is the second frame drawn in the corpus.
 */
export const FRAME_MAX_PX = 430;

/**
 * The geometry every phone frame shares.
 *
 * `100svh` and not `100vh`: on a phone `100vh` is the height the window has with the
 * address bar HIDDEN, so a `100vh` box is taller than the visible area from the moment
 * the page loads and its last rows sit under the browser's own chrome. `svh` is the
 * small viewport — the height that is visible when the chrome is showing — which is the
 * one measurement that is true the whole time. This is the same class of mistake as the
 * fixed 680 and would have replaced it with a subtler one.
 */
/*
 * WRITTEN OUT, NOT INTERPOLATED. Tailwind finds classes by scanning source TEXT, so
 * `max-w-[${FRAME_MAX_PX}px]` generates no rule at all and the cap silently does not
 * exist — the frame would stretch to the full window on a desktop and look, at a glance,
 * like it was working. `__tests__/frame.test.ts` asserts this string carries exactly
 * `FRAME_MAX_PX`, so the two cannot drift.
 */
export const FRAME_BOX =
  'w-full max-w-[430px] h-[100svh] mx-auto box-border flex flex-col';

/**
 * The same box for a frame the design gives an explicit height to and whose content is
 * known to fit — the onboarding and state frames, where the drawn height is part of the
 * composition rather than a container for a list.
 *
 * It is still `min-h`, never `h`. A frame that cannot grow is a frame that cuts, and the
 * one screen the founder photographed was a state frame whose content ran past 680.
 */
export const FRAME_BOX_FIXED_DESIGN_HEIGHT = FRAME_BOX;

/**
 * A frame holding a DOCUMENT rather than an app screen — the policy pages.
 *
 * These were already `min-h-[680px]` rather than `h-[680px]`, and that difference was
 * correct: terms of service is a long text somebody scrolls through, not a composition
 * with a tab bar pinned under it. Page scroll is the right behaviour for a document and
 * the wrong behaviour for a screen, so the two are kept apart rather than unified into
 * one frame that is slightly wrong for both.
 */
export const FRAME_BOX_DOCUMENT =
  'w-full max-w-[430px] min-h-[100svh] mx-auto box-border flex flex-col';

/**
 * A screen the manifest declares at a desk width, opened on a phone.
 *
 * The ops console (1180) and the founder screens (1440) are desktop products; the design
 * draws no phone layout for them and one must not be invented. But a desktop screen on a
 * phone was pushing the WHOLE DOCUMENT sideways by up to 1120px, which breaks the page
 * itself — every other screen's scroll position, the tab bar, the browser's own gesture.
 *
 * So the wide frame is put in a horizontal scroller of its own. The screen is unchanged
 * and still needs a desk; it is now something a phone can pan across instead of something
 * that breaks the document it is in. That is the difference between "not designed for
 * this device" and "broken".
 */
export const WIDE_FRAME_SCROLLER = 'w-full max-w-full overflow-x-auto';
